import { AUTH_API_URL } from './constants';

const TOKEN_KEY = 'signus.authTokens';

export interface AuthUser {
  email: string;
  firstName: string;
  lastName: string | null;
  accessToken: string;
  refreshToken: string;
  teamSlug: string;
}

export type MfaMethod = 'EMAIL' | 'VIRTUAL' | 'SMS';

export interface MfaDetail {
  id: string;
  hint: string | null;
  type: MfaMethod;
}

/** Thrown by `loginWithCredentials` when the account has MFA enabled and no (or an invalid) code was supplied. */
export class MfaRequiredError extends Error {
  methods: MfaDetail[];

  constructor(methods: MfaDetail[]) {
    super('Verification code required.');
    this.name = 'MfaRequiredError';
    this.methods = methods;
  }
}

export function getStoredUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(TOKEN_KEY);
    return raw ? (JSON.parse(raw) as AuthUser) : null;
  } catch {
    return null;
  }
}

function storeUser(user: AuthUser): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(user));
}

export function clearStoredUser(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Fires when a refresh attempt proves the session is dead (refresh token expired or revoked).
 *
 * Most callers of `refreshTokens` pass their own `onAuthChange` and don't need this. The one
 * that can't is `fetchWithTokenRefresh` in `apiFetch.ts` — it runs deep inside service calls
 * with no React state in scope, so without this broadcast it would clear localStorage while the
 * pane kept rendering the signed-in UI, leaving every subsequent action to fail with no
 * explanation until reload. App subscribes and drops to the signed-out screen instead.
 */
const sessionEndedListeners = new Set<() => void>();

export function onSessionEnded(listener: () => void): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

function mapPayload(payload: Record<string, unknown>): AuthUser {
  const team = payload.team as Record<string, unknown> | undefined;
  return {
    email: payload.email as string,
    firstName: payload.firstName as string,
    lastName: (payload.lastName as string | null) ?? null,
    accessToken: payload.accessToken as string,
    refreshToken: payload.refreshToken as string,
    teamSlug: (team?.teamSlug as string) ?? '',
  };
}

const FRIENDLY_ERROR_MESSAGES: Record<string, string> = {
  MFA_INVALID_CODE_ERR: 'That code is incorrect. Double-check it and try again.',
  MFA_CODE_EXPIRED_ERR: 'That code has expired. Request a new one and try again.',
};

function describeError(json: Record<string, unknown>, status: number): string {
  const firstError = (json?.errors as Record<string, unknown>[] | undefined)?.[0];
  const type = firstError?.type as string | undefined;
  if (type && FRIENDLY_ERROR_MESSAGES[type]) {
    return FRIENDLY_ERROR_MESSAGES[type];
  }

  const raw = (firstError?.errorMessage || json?.errorMessage || json?.message) as
    | string
    | undefined;
  if (raw) {
    return raw.replace(/^[A-Za-z][\w.$]*Exception:\s*/, '');
  }

  return `Request failed (${status})`;
}

async function post<T>(path: string, body: unknown, headers?: Record<string, string>): Promise<T> {
  const res = await fetch(`${AUTH_API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify(body),
  });

  const json = await res.json();

  if (!res.ok) {
    const firstError = json?.errors?.[0];

    if (firstError?.type === 'INVALID_MFA_ERR') {
      const methods: MfaDetail[] = ((firstError.args ?? []) as Record<string, unknown>[]).map(
        m => ({
          id: m.id as string,
          hint: (m.hint as string | null) ?? null,
          type: m.type as MfaMethod,
        }),
      );
      throw new MfaRequiredError(methods);
    }

    if (res.status === 401) {
      throw new Error('Email or password is incorrect.');
    }
    throw new Error(describeError(json, res.status));
  }

  return json as T;
}

/**
 * Sign in with email + password. When the account has MFA enabled, the first call (without
 * `otp`/`mfa`) rejects with `MfaRequiredError` listing the available methods (and, for
 * EMAIL/SMS, triggers sending the default method's code) — the caller re-invokes this with the
 * code the user entered and the chosen method to complete sign-in.
 */
export async function loginWithCredentials(
  email: string,
  password: string,
  otp?: string,
  mfa?: MfaMethod,
): Promise<AuthUser> {
  const data = await post<{ payload: Record<string, unknown> }>('/v0/users/login', {
    email,
    password,
    ...(otp ? { otp, mfa } : {}),
  });
  const user = mapPayload(data.payload);
  storeUser(user);
  return user;
}

/**
 * Exchange a Google ID token (from the sign-in dialog — see `services/googleAuth.ts`) for a
 * Signus session. auth-service verifies the token's signature and audience itself, so the
 * client never has to be trusted about who the user is.
 */
export async function loginWithGoogle(idToken: string): Promise<AuthUser> {
  const data = await post<{ payload: Record<string, unknown> }>('/v0/google/id-login', {
    idToken,
  });
  const user = mapPayload(data.payload);
  storeUser(user);
  return user;
}

/** Resend an MFA verification code (EMAIL or SMS methods only — VIRTUAL codes come from the user's authenticator app). */
export async function sendMfaCode(
  method: Extract<MfaMethod, 'EMAIL' | 'SMS'>,
  mfaId: string,
  email: string,
  password: string,
): Promise<void> {
  await post(`/v0/mfa/${method === 'EMAIL' ? 'email' : 'sms'}/send`, { mfaId, email, password });
}

export async function generateMagicLink(accessToken: string): Promise<string> {
  const res = await fetch(`${AUTH_API_URL}/v0/hubspot/user-app/magic-link/generate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: accessToken },
    body: JSON.stringify({}),
  });

  const json = await res.json();

  if (!res.ok) {
    throw new Error(
      json?.errorMessage || json?.message || `Magic link generation failed (${res.status})`,
    );
  }

  const url: string | undefined = json?.payload?.url;
  if (!url) {
    throw new Error('Magic link generation failed — no URL in response');
  }

  return url;
}

export async function refreshTokens(
  user: AuthUser,
  onAuthChange?: (user: AuthUser | null) => void,
): Promise<AuthUser | null> {
  const res = await fetch(`${AUTH_API_URL}/v0/users/me/refresh-token`, {
    method: 'GET',
    headers: { Authorization: user.refreshToken },
  });

  if (!res.ok) {
    clearStoredUser();
    onAuthChange?.(null);
    sessionEndedListeners.forEach(listener => listener());
    return null;
  }

  const json = await res.json();
  const refreshed = mapPayload(json.payload);
  storeUser(refreshed);
  onAuthChange?.(refreshed);
  return refreshed;
}
